import hostyAppIcon from '@assets/images/hosty-app-icon.png';
import { FormEvent, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ApiRequestError } from '../../api/client';
import { Alert } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { usePricing } from '../../hooks/usePricing';
import { Input, Select } from '../../components/ui/Input';
import { useAuth } from '../../context/AuthContext';
import { storeLoginCredential } from '../../utils/credentials';
import { trackEvent } from '../../utils/tracking';
import { GeneratePasswordAction } from '../../components/ui/GeneratePasswordAction';
import './register-page.css';

const BENEFITS = [
  {
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <rect x="2" y="3" width="20" height="14" rx="2" />
        <path d="M8 21h8M12 17v4" />
      </svg>
    ),
    title: 'TPV para Windows',
    desc: 'App de escritorio rápida con catálogo, carrito y cobro en efectivo o tarjeta.',
  },
  {
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
        <polyline points="9 22 9 12 15 12 15 22" />
      </svg>
    ),
    title: 'Plano de mesas y cocina',
    desc: 'Gestiona comensales, cuentas aparcadas y comandas de cocina en tiempo real.',
  },
  {
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
      </svg>
    ),
    title: 'Informes en la nube',
    desc: 'Ventas, ticket medio, métodos de pago y cierres de caja desde cualquier lugar.',
  },
  {
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <circle cx="12" cy="12" r="10" />
        <path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
      </svg>
    ),
    title: 'Multi-local',
    desc: 'Varias licencias gestionadas desde un único panel web. Todos los módulos, incluido VeriFactu.',
  },
  {
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <rect x="2" y="6" width="14" height="12" rx="2" />
        <path d="M14 10h4l3 3v3h-7V10z" />
        <circle cx="6.5" cy="18.5" r="1.5" />
        <circle cx="17.5" cy="18.5" r="1.5" />
      </svg>
    ),
    title: 'Deliveries integrados',
    desc: 'Recibe pedidos de Glovo, Uber Eats, Just Eat y más directamente en el TPV.',
    tag: 'Próximamente',
  },
  {
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <polyline points="14 2 14 8 20 8" />
        <line x1="9" y1="15" x2="15" y2="15" />
        <line x1="9" y1="11" x2="11" y2="11" />
        <rect x="13" y="10" width="5" height="3" rx="1.5" />
        <circle cx="15.5" cy="11.5" r="1" fill="currentColor" stroke="none" />
      </svg>
    ),
    title: 'VeriFactu incluido',
    desc: 'Configura los datos fiscales de tu negocio y completa la autorización para enviar facturas.',
    tag: 'Incluido',
  },
];

export function RegisterPage() {
  const pricing = usePricing();
  const { register, loginDemo } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState<{
    email: string;
    password: string;
    name: string;
    surname: string;
    representationEntityType: 'fisica' | 'juridica';
    representativeNif: string;
  }>({
    email: '',
    password: '',
    name: '',
    surname: '',
    representationEntityType: 'fisica',
    representativeNif: '',
  });
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [confirmError, setConfirmError] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [demoLoading, setDemoLoading] = useState(false);

  function validatePasswordMatch(password: string, confirm: string) {
    if (!confirm) return '';
    return password === confirm ? '' : 'Las contraseñas no coinciden';
  }

  const passwordGenerateAction = (
    <GeneratePasswordAction
      disabled={loading || demoLoading}
      onGenerate={(password) => {
        setForm((prev) => ({ ...prev, password }));
        setPasswordConfirm(password);
        setConfirmError('');
      }}
    />
  );

  async function handleDemoLogin() {
    setError('');
    setDemoLoading(true);
    try {
      await loginDemo();
      navigate('/panel');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo abrir la demo');
    } finally {
      setDemoLoading(false);
    }
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError('');
    setMessage('');

    const formData = new FormData(event.currentTarget);
    const email = String(formData.get('username') ?? form.email).trim();
    const password = String(formData.get('new-password') ?? form.password);
    const confirm = String(formData.get('confirm-new-password') ?? passwordConfirm);

    const mismatch = validatePasswordMatch(password, confirm);
    if (mismatch) {
      setConfirmError(mismatch);
      return;
    }

    setConfirmError('');
    setForm((prev) => ({ ...prev, email, password }));
    setLoading(true);

    const payload = { ...form, email, password, name: form.name, surname: form.surname };

    try {
      const result = await register(payload);
      trackEvent('registro_completado', { metodo: 'formulario_web' });
      setMessage(result);
      await storeLoginCredential(email, password);
    } catch (err) {
      if (
        err instanceof ApiRequestError &&
        err.message.includes('Cuenta creada, pero no se pudo enviar el correo')
      ) {
        setMessage(err.message);
        await storeLoginCredential(email, password);
        return;
      }
      setError(err instanceof Error ? err.message : 'No se pudo completar el registro');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="register-split">
      {/* Panel izquierdo — argumentario de venta */}
      <aside className="register-benefits" aria-label="Beneficios de TPV Hosty">
        <div className="register-benefits__inner">
          <div className="register-benefits__brand">
            <img src={hostyAppIcon} alt="" className="brand__mark" width={32} height={32} />
            <span className="register-benefits__brand-name">
              TPV <strong>Hosty</strong>
            </span>
          </div>

          <h2 className="register-benefits__headline">
            Gestiona tu negocio desde el mostrador
            <br />
            <span>y desde cualquier lugar</span>
          </h2>

          <ul className="register-benefits__list">
            {BENEFITS.map((b) => (
              <li key={b.title} className="register-benefits__item">
                <span className="register-benefits__icon">{b.icon}</span>
                <div>
                  <span className="register-benefits__item-header">
                    <strong>{b.title}</strong>
                    {'tag' in b && b.tag ? (
                      <span className="register-benefits__tag">{b.tag}</span>
                    ) : null}
                  </span>
                  <p>{b.desc}</p>
                </div>
              </li>
            ))}
          </ul>

          <div className="register-benefits__price">
            <span className="register-benefits__price-amount">{pricing.amount} €<small>/mes</small></span>
            <div className="register-benefits__price-detail">
              Autogestionado · Sin permanencia · IVA incl.
              <br />
              Todos los módulos, incluido VeriFactu
            </div>
          </div>

          <p className="register-benefits__commitment">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
            </svg>
            Instalación y soporte técnico aparte. Pagos integrados opcionales con tarifa por operación.
          </p>
        </div>
      </aside>

      {/* Panel derecho — formulario */}
      <div className="register-form-col">
        <div className="auth-card">
          <h1>Crear cuenta</h1>
          <p className="muted">Empieza con Hosty autogestionado y sin permanencia.</p>
          {message ? (
            <div style={{ marginTop: '1rem' }}>
              <Alert tone="success">{message}</Alert>
            </div>
          ) : null}
          {error ? (
            <div style={{ marginTop: '1rem' }}>
              <Alert tone="danger">{error}</Alert>
            </div>
          ) : null}
          <form
            id="hosty-register-form"
            method="post"
            action="/registro"
            autoComplete="on"
            onSubmit={handleSubmit}
          >
            <Input
              label="Nombre"
              id="register-given-name"
              name="given-name"
              type="text"
              autoComplete="given-name"
              enterKeyHint="next"
              required
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
            <Input
              label="Apellidos"
              id="register-family-name"
              name="family-name"
              type="text"
              autoComplete="family-name"
              enterKeyHint="next"
              required
              value={form.surname}
              onChange={(e) => setForm({ ...form, surname: e.target.value })}
            />
            <Select
              label="Tipo de titular fiscal"
              value={form.representationEntityType}
              onChange={(e) =>
                setForm({
                  ...form,
                  representationEntityType: e.target.value as 'fisica' | 'juridica',
                })
              }
              options={[
                { value: 'fisica', label: 'Persona física / autónomo' },
                { value: 'juridica', label: 'Persona jurídica / sociedad' },
              ]}
            />
            <Input
              label={
                form.representationEntityType === 'juridica'
                  ? 'NIF del representante legal'
                  : 'NIF del titular'
              }
              value={form.representativeNif}
              onChange={(e) =>
                setForm({ ...form, representativeNif: e.target.value.toUpperCase() })
              }
              autoCapitalize="characters"
              required
              maxLength={20}
              hint={
                form.representationEntityType === 'juridica'
                  ? 'El nombre y los apellidos anteriores deben ser los del representante legal que firmará.'
                  : 'Se utilizará junto con tu nombre y apellidos en el modelo de representación.'
              }
            />
            <Input
              label="Email"
              id="username"
              name="username"
              type="email"
              inputMode="email"
              autoComplete="username"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              enterKeyHint="next"
              required
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
            />
            <Input
              label="Contraseña"
              id="register-new-password"
              name="new-password"
              type="password"
              showPasswordToggle
              autoComplete="new-password"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              enterKeyHint="next"
              required
              minLength={8}
              hint="Mínimo 8 caracteres"
              labelAction={passwordGenerateAction}
              value={form.password}
              onChange={(e) => {
                const password = e.target.value;
                setForm({ ...form, password });
                if (passwordConfirm) {
                  setConfirmError(validatePasswordMatch(password, passwordConfirm));
                }
              }}
            />
            <Input
              label="Repetir contraseña"
              id="register-confirm-password"
              name="confirm-new-password"
              type="password"
              showPasswordToggle
              autoComplete="new-password"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              enterKeyHint="done"
              required
              minLength={8}
              error={confirmError}
              labelAction={passwordGenerateAction}
              value={passwordConfirm}
              onChange={(e) => {
                const confirm = e.target.value;
                setPasswordConfirm(confirm);
                setConfirmError(validatePasswordMatch(form.password, confirm));
              }}
              onBlur={() => {
                if (passwordConfirm) {
                  setConfirmError(validatePasswordMatch(form.password, passwordConfirm));
                }
              }}
            />
            <Button type="submit" loading={loading} disabled={demoLoading}>
              Crear cuenta
            </Button>
          </form>
          <div className="auth-demo">
            <p className="muted">¿Prefieres probar el panel antes de registrarte?</p>
            <Button
              type="button"
              variant="secondary"
              loading={demoLoading}
              disabled={loading}
              onClick={handleDemoLogin}
            >
              Entrar con cuenta demo
            </Button>
          </div>
          <p className="auth-card__footer">
            {message ? (
              <>
                ¿No has recibido el correo?{' '}
                <Link to={`/reenviar-verificacion?email=${encodeURIComponent(form.email)}`}>
                  Reenviar verificación
                </Link>
                <br />
              </>
            ) : null}
            ¿Ya tienes cuenta? <Link to="/login">Inicia sesión</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
